import React from 'react';
import { motion } from 'framer-motion';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const CateringServices = () => {
  const services = [
    {
      id: 1,
      icon: 'Briefcase',
      title: 'Corporate Catering',
      description: 'Impress clients and energize your team with professional catering for meetings, conferences, and corporate events. Flexible delivery times and setup options.',
      image: 'https://images.unsplash.com/photo-1511578314322-379afb476865',
      imageAlt: 'Professional corporate event with catered Mexican food buffet and business attendees',
      features: ['Breakfast & Lunch Options', 'On-Time Delivery', 'Professional Setup', 'Dietary Accommodations']
    },
    {
      id: 2,
      icon: 'Heart',
      title: 'Wedding Services',
      description: 'Make your special day unforgettable with customized wedding catering. From cocktail hour to full reception service, we create memorable dining experiences.',
      image: 'https://images.unsplash.com/photo-1519225421980-715cb0215aed',
      imageAlt: 'Elegant wedding reception with beautifully catered Mexican cuisine and festive decorations',
      features: ['Custom Menus', 'Tasting Sessions', 'Full Service Staff', 'Bar Services Available']
    },
    {
      id: 3,
      icon: 'Users',
      title: 'Private Parties',
      description: 'Celebrate birthdays, anniversaries, graduations, or any special occasion with delicious catering that brings people together.',
      image: 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d',
      imageAlt: 'Festive private party with colorful Mexican food spread and happy guests celebrating',
      features: ['Flexible Packages', 'Setup & Cleanup', 'Serving Staff Available', 'Party Supplies']
    },
    {
      id: 4,
      icon: 'Calendar',
      title: 'Holiday Events',
      description: 'From Cinco de Mayo to company holiday parties, we specialize in festive catering that captures the spirit of celebration.',
      image: '/assets/images/image-1768969419100.png',
      imageAlt: 'Family gathering by the lake enjoying holiday celebration with food and togetherness',
      features: ['Seasonal Menus', 'Themed Decorations', 'Large Group Capacity', 'Advance Booking']
    }
  ];

  return (
    <section className="py-16 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
            <Icon name="Sparkles" size={20} className="text-primary" />
            <span className="text-primary font-body font-medium">OUR SERVICES</span>
          </div>

          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mb-4">
            Catering for <span className="text-primary">Every Occasion</span>
          </h2>

          <p className="text-lg text-muted-foreground font-body max-w-2xl mx-auto">
            Professional catering services tailored to your event's unique needs
          </p>
        </motion.div>

        <div className="space-y-12">
          {services?.map((service, index) => (
            <motion.div
              key={service?.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className={`grid lg:grid-cols-2 gap-8 items-center ${
                index % 2 === 1 ? 'lg:flex-row-reverse' : ''
              }`}
            >
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <div className="relative rounded-2xl overflow-hidden shadow-warm-xl">
                  <Image
                    src={service?.image}
                    alt={service?.imageAlt}
                    className="w-full h-[400px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                </div>
              </div>

              <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mb-4">
                  <Icon name={service?.icon} size={32} className="text-primary" />
                </div>
                <h3 className="text-3xl font-heading font-bold text-foreground mb-4">
                  {service?.title}
                </h3>
                <p className="text-lg text-muted-foreground font-body mb-6 leading-relaxed">
                  {service?.description}
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {service?.features?.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <Icon name="Check" size={20} className="text-success" />
                      <span className="text-foreground font-body">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CateringServices;